import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, MapPin, MapPinOff, Pencil } from 'lucide-react';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import {
  SENSOR_DEVICE_STALE_AFTER_HOURS,
  SensorDevice,
  canBindToLocation,
  formatBoundPen,
  isBindable,
  isSensorDeviceStale,
  sensorDeviceTypeLabelKey
} from '../../models/sensor-devices';

/**
 * Every native button style is reset by hand - see `DeviceSyncStatus` for why. Only the colours differ
 * between the three variants below.
 */
const iconButtonBase =
  'flex h-8 w-8 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-md border bg-white p-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-default disabled:opacity-60';

/**
 * The three row actions, coloured by what they are rather than uniformly.
 *
 * Indigo is this app's "primary, do this" colour (`pigletRegistrationColors.registered`, every focus ring),
 * so it goes on **bind** - the only action on this screen that represents outstanding work, on exactly the
 * rows the amber "No pen" badge has already flagged. **Change pen** stays grey: a device that is already
 * bound needs nothing, and colouring routine maintenance the same as outstanding work would flatten the
 * distinction the badge just made. **Unbind** carries red, the house colour for a destructive action
 * (`DeleteOutline color="error"` in `ExternalProviders`) - muted at rest so a bound row does not shout, full
 * strength on hover.
 */
const iconButtonVariants = {
  bind: 'border-indigo-200 text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700',
  edit: 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900',
  unbind: 'border-gray-200 text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700'
} as const;

/**
 * What a measured tag is, by hue - so "which pens measure CO2" is answerable by scanning rather than reading.
 *
 * Three rules hold this together.
 *
 * **Hue follows the quantity** the way a farmer already thinks about it: heat amber, water sky, gas violet.
 *
 * **Matched on a substring, not on the whole string.** These are the provider's own labels and the backend
 * does not validate them, so the vocabulary is open-ended - this farm alone sends `Co2`, `Nh3`,
 * `HeatIndexChickens` and `HeatIndexPigs`. An exact-match table would colour the first two and drop the
 * heat indices into grey despite their being heat measures, and would need editing every time Whysor adds a
 * derived value.
 *
 * **An unrecognised tag stays grey.** A tag matching no rule gets no colour rather than a guessed one - the
 * same reason the labels themselves are printed verbatim instead of translated.
 *
 * Borderless on purpose. The status markers in the neighbouring columns are outlined filled badges, and amber
 * already means "needs attention" there; giving categories a visibly different chip shape keeps a
 * "Temperature" tag from reading as a warning about the row.
 */
const tagColorRules: [RegExp, string][] = [
  [/temp|heatindex/, 'bg-amber-50 text-amber-700'],
  [/humidity|dewpoint/, 'bg-sky-50 text-sky-700'],
  [/co2|carbondioxide/, 'bg-violet-50 text-violet-700'],
  [/nh3|ammonia/, 'bg-emerald-50 text-emerald-700'],
  [/rain|precipitation/, 'bg-blue-50 text-blue-700'],
  [/pressure/, 'bg-teal-50 text-teal-700'],
  [/wind/, 'bg-cyan-50 text-cyan-700'],
  [/light|lux|radiation/, 'bg-yellow-50 text-yellow-700']
];

const UNKNOWN_TAG_COLOR = 'bg-gray-100 text-gray-600';

/** Case- and separator-insensitive: the provider sends `CO2`, `co2`, `Nh3` and `HeatIndexPigs` alike. */
const tagColor = (tag: string): string => {
  const normalised = tag.toLowerCase().replace(/[\s_-]/g, '');

  return tagColorRules.find(([pattern]) => pattern.test(normalised))?.[1] ?? UNKNOWN_TAG_COLOR;
};

/**
 * Which pen the device is bound to, or the fact that it is bound to none.
 *
 * Unbound is not an error - a device the sync has never managed to place is in exactly this state - but it is
 * the one row state that needs someone to act, so it is the one status in the table wearing a colour. The
 * amber fill is a step stronger than the category pills two columns over, so a status reads as a status
 * rather than as another label.
 */
const BoundPenCell: React.FC<{ device: SensorDevice; retiredOn: string | null }> = ({ device, retiredOn }) => {
  const { t } = useTranslation();

  // A weather station has no pen and never will, so flagging it amber would be an alarm nobody can act on.
  // It gets the same neutral dash as any other not-applicable cell.
  if (!canBindToLocation(device)) {
    return (
      <span className="text-gray-300" title={t('f-sensor-dev-weather-hint')}>
        —
      </span>
    );
  }

  // Retired is a terminal state, not a queue: the provider stopped listing the device, so it holds no pen and
  // will receive nothing further. Slate rather than amber - there is nothing here for anyone to clear.
  if (device.isRetired) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700"
        title={retiredOn ? t('f-sensor-dev-retired-on', { date: retiredOn }) : t('f-sensor-dev-retired-hint')}
      >
        <Ban className="h-3 w-3" aria-hidden="true" />
        {t('f-sensor-dev-retired')}
      </span>
    );
  }

  if (!device.locationId) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
        title={t('f-sensor-dev-unbound-hint')}
      >
        <MapPinOff className="h-3 w-3" aria-hidden="true" />
        {t('f-sensor-dev-unbound')}
      </span>
    );
  }

  const pen = formatBoundPen(device);

  return (
    <span className="flex flex-col">
      <span className="text-gray-800">{pen || t('f-com-unknown')}</span>
      {device.locationNumber && <span className="font-mono! text-[11px] text-gray-400">{device.locationNumber}</span>}
    </span>
  );
};

interface SensorDevicesTableProps {
  devices: SensorDevice[];
  /** Disabled while a write is in flight, so a double click cannot queue two conflicting bindings. */
  isBusy: boolean;
  onEditLocation: (device: SensorDevice) => void;
  onUnassignLocation: (device: SensorDevice) => void;
}

/**
 * One row per device the provider knows about, in the order the reader's questions come: which device is this,
 * what kind of thing is it, what does it measure, which pen is it in, and when was it last heard from.
 *
 * The device name leads because it is the only column a farmer can match against the barn - the provider knows
 * the customer's organization and whatever free text somebody typed at it, and nothing else. Its external id
 * sits underneath in a mono face for the case where two devices were given the same name.
 */
const SensorDevicesTable: React.FC<SensorDevicesTableProps> = ({
  devices,
  isBusy,
  onEditLocation,
  onUnassignLocation
}) => {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  return (
    <div className="overflow-x-auto">
      {/* `border-collapse` by hand: Tailwind runs without preflight here, so cells default to border-spacing
          and the grey header shows a seam between every column. */}
      <table className="w-full border-collapse text-sm">
        <thead className="border-b border-gray-100 bg-gray-50">
          <tr>
            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">{t('f-sensor-dev-col-name')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-sensor-dev-col-type')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-sensor-dev-col-measures')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-sensor-dev-col-pen')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-com-date')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-com-time')}</th>
            <th className="px-6 py-2 text-right text-xs font-medium text-gray-500">{t('f-com-actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {devices.map((device) => {
            const isStale = isSensorDeviceStale(device);
            const canBind = isBindable(device);

            return (
              <tr key={device.id} className="transition-colors hover:bg-gray-50">
                <td className="px-6 py-2.5">
                  <span className="font-medium text-gray-900">{device.externalName || t('f-com-unknown')}</span>
                  {/* `font-mono!`, not `font-mono`: index.css sets the Roboto face on `*` outside any cascade
                      layer, so the family only sticks when marked important. */}
                  <span className="block font-mono! text-[11px] text-gray-400">{device.externalId}</span>
                </td>

                <td className="px-4 py-2.5 text-gray-600">
                  {/* The device-type question, not the "can it be bound now" one: a retired climate box is
                      still a climate box, and the weather-station hint would be a lie about it. */}
                  <span title={canBindToLocation(device) ? undefined : t('f-sensor-dev-weather-hint')}>
                    {t(sensorDeviceTypeLabelKey(device.deviceType))}
                  </span>
                  {/* Provider then source tag: "Whysor · kpn-lora". The provider is the service the farm
                      configured and is the same for every row today, but it is the thing a reader needs when
                      a second one is added - and the domain alone (`kpn-lora`, `visual-crossing`) means
                      nothing without it. */}
                  <span className="block text-[11px] text-gray-400">
                    {[device.provider, device.externalDomain].filter((part) => !!part).join(' · ')}
                  </span>
                </td>

                {/* The provider's own tags, printed verbatim: the backend does not validate them, so
                    translating them would invent meaning the API never promised. */}
                <td className="px-4 py-2.5">
                  {device.measuredTags.length === 0 ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {device.measuredTags.map((tag) => (
                        <span
                          key={tag}
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tagColor(tag)}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </span>
                  )}
                </td>

                <td className="px-4 py-2.5">
                  {/* The retirement date is formatted here, where the farm-timezone hook lives, rather than
                      inside the cell - `useFarmDateTime` would otherwise be called once per row. */}
                  <BoundPenCell device={device} retiredOn={device.retiredAt ? formatDate(device.retiredAt) : null} />
                </td>

                {/* Date and time in their own columns, in farm time, the way the medical record table does it.
                    The dot is the stale flag: past two missed syncs the provider has stopped returning this
                    device, and its charts are standing still whatever the binding says. */}
                <td className="px-4 py-2.5 text-xs text-gray-500">
                  {device.lastSeenAt ? (
                    <span className="inline-flex items-center gap-1.5">
                      {isStale && (
                        <span
                          aria-hidden
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          title={t('f-sensor-dev-stale-hint', { hours: SENSOR_DEVICE_STALE_AFTER_HOURS })}
                        />
                      )}
                      <span title={isStale ? t('f-sensor-dev-stale') : undefined}>{formatDate(device.lastSeenAt)}</span>
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500">
                  {device.lastSeenAt ? formatTime(device.lastSeenAt) : <span className="text-gray-300">—</span>}
                </td>

                {/* A weather station is an online service, not hardware in a barn: it can never hold a pen, so
                    it gets no action rather than an action that fails. */}
                <td className="px-6 py-2.5">
                  {canBind ? (
                    <span className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => onEditLocation(device)}
                        disabled={isBusy}
                        title={t(device.locationId ? 'f-sensor-dev-reassign' : 'f-sensor-dev-assign')}
                        aria-label={t(device.locationId ? 'f-sensor-dev-reassign' : 'f-sensor-dev-assign')}
                        className={`${iconButtonBase} ${
                          device.locationId ? iconButtonVariants.edit : iconButtonVariants.bind
                        }`}
                      >
                        {device.locationId ? (
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                      </button>

                      {device.locationId && (
                        <button
                          type="button"
                          onClick={() => onUnassignLocation(device)}
                          disabled={isBusy}
                          title={t('f-sensor-dev-unassign')}
                          aria-label={t('f-sensor-dev-unassign')}
                          className={`${iconButtonBase} ${iconButtonVariants.unbind}`}
                        >
                          <MapPinOff className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="block text-right text-gray-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SensorDevicesTable;
